import { useEffect, useState, type FormEvent } from "react";
import toast from "react-hot-toast";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { supabase } from "@/lib/supabase";
import { withTimeout } from "@/lib/timeout";
import { ROLE_LABEL } from "@/lib/roles";

function validatePassword(pw: string): string | null {
  if (pw.length < 12) return "Use at least 12 characters.";
  let classes = 0;
  if (/[a-z]/.test(pw)) classes++;
  if (/[A-Z]/.test(pw)) classes++;
  if (/[0-9]/.test(pw)) classes++;
  if (/[^A-Za-z0-9]/.test(pw)) classes++;
  if (classes < 3) return "Mix upper, lower, number, and symbol (any 3 of 4).";
  return null;
}

type TotpFactor = {
  id: string;
  friendly_name?: string;
  status?: string;
};

export function SettingsProfile() {
  const user = useCurrentUser();
  const [factors, setFactors] = useState<TotpFactor[]>([]);
  const [qrCodeSvg, setQrCodeSvg] = useState<string | null>(null);
  const [pendingFactorId, setPendingFactorId] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [busy, setBusy] = useState(false);

  // Change-password form state
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  async function refreshFactors() {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) {
      toast.error(error.message);
      return;
    }
    setFactors((data?.totp ?? []) as TotpFactor[]);
  }

  useEffect(() => {
    void refreshFactors();
  }, []);

  async function startEnrollTotp() {
    setBusy(true);
    try {
      const { data, error } = await withTimeout(
        supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: "Google Authenticator" }),
        15_000,
        "Authenticator setup",
      );
      if (error) {
        toast.error(error.message);
        return;
      }
      setPendingFactorId(data.id);
      setQrCodeSvg(data.totp.qr_code);
      toast.success("Scan QR code in Google Authenticator, then enter the 6-digit code.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't start setup.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyEnrollTotp() {
    if (!pendingFactorId) return;
    if (!totpCode.trim()) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    setBusy(true);
    try {
      const { data: challenge, error: challengeError } = await withTimeout(
        supabase.auth.mfa.challenge({ factorId: pendingFactorId }),
        15_000,
        "Authenticator challenge",
      );
      if (challengeError) {
        toast.error(challengeError.message);
        return;
      }
      const { error: verifyError } = await withTimeout(
        supabase.auth.mfa.verify({
          factorId: pendingFactorId,
          challengeId: challenge.id,
          code: totpCode.trim(),
        }),
        15_000,
        "Authenticator verify",
      );
      if (verifyError) {
        toast.error(verifyError.message);
        return;
      }
      setPendingFactorId(null);
      setQrCodeSvg(null);
      setTotpCode("");
      toast.success("Google Authenticator enabled.");
      await refreshFactors();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't verify code.");
    } finally {
      setBusy(false);
    }
  }

  async function disableFactor(factorId: string) {
    const ok = window.confirm(
      "Disable Google Authenticator? You'll only need your password to sign in until you enable it again.",
    );
    if (!ok) return;
    setBusy(true);
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Authenticator disabled.");
    await refreshFactors();
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="text-sm font-semibold text-slate-900">Account</h3>
        <dl className="mt-4 grid grid-cols-3 gap-3 text-sm">
          <dt className="text-slate-500">Name</dt>
          <dd className="col-span-2 text-slate-900">{user?.fullName ?? "—"}</dd>
          <dt className="text-slate-500">Email</dt>
          <dd className="col-span-2 text-slate-900">{user?.email ?? "—"}</dd>
          <dt className="text-slate-500">Role</dt>
          <dd className="col-span-2 text-slate-900">{user?.role ? ROLE_LABEL[user.role] : "—"}</dd>
        </dl>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="text-sm font-semibold text-slate-900">Security</h3>
        <p className="mt-1 text-xs text-slate-500">Google Authenticator (TOTP) for login protection.</p>

        <div className="mt-4 space-y-3">
          {factors.length === 0 && !pendingFactorId && (
            <button
              type="button"
              onClick={startEnrollTotp}
              disabled={busy}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              Enable Google Authenticator
            </button>
          )}

          {factors.length > 0 && (
            <div className="space-y-2">
              {factors.map((factor) => (
                <div key={factor.id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{factor.friendly_name ?? "Google Authenticator"}</p>
                    <p className="text-xs text-slate-500">Status: {factor.status ?? "verified"}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => disableFactor(factor.id)}
                    disabled={busy}
                    className="rounded-md border border-rose-300 bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                  >
                    Disable
                  </button>
                </div>
              ))}
            </div>
          )}

          {pendingFactorId && qrCodeSvg && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <p className="mb-2 text-xs text-slate-600">Scan this QR in Google Authenticator, then verify:</p>
              <div className="inline-block rounded bg-white p-2" dangerouslySetInnerHTML={{ __html: qrCodeSvg }} />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="w-28 rounded-md border-slate-300 px-2 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
                />
                <button
                  type="button"
                  onClick={verifyEnrollTotp}
                  disabled={busy}
                  className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  Verify
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="text-sm font-semibold text-slate-900">Change Password</h3>
        <p className="mt-1 text-xs text-slate-500">At least 12 characters with upper, lower, number, and symbol (any 3 of 4).</p>
        <form
          onSubmit={async (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const err = validatePassword(newPw);
            if (err) { toast.error(err); return; }
            if (newPw !== confirmPw) { toast.error("Passwords don't match."); return; }
            setPwBusy(true);
            const { data, error } = await withTimeout(
              supabase.functions.invoke<{ ok: boolean; error?: string; detail?: string }>(
                "apply-password-reset",
                { body: { password: newPw } },
              ),
              20_000,
              "Change password",
            );
            setPwBusy(false);
            if (error || !data?.ok) {
              toast.error(data?.detail ?? data?.error ?? error?.message ?? "Failed to update password");
              return;
            }
            setNewPw("");
            setConfirmPw("");
            toast.success("Password updated.");
          }}
          className="mt-4 space-y-3"
        >
          <label className="block">
            <span className="text-xs font-medium text-slate-700">New password</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className="mt-1 block w-full max-w-sm rounded-md border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">Confirm password</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className="mt-1 block w-full max-w-sm rounded-md border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </label>
          <button
            type="submit"
            disabled={pwBusy || !newPw || !confirmPw}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {pwBusy ? "Saving…" : "Save password"}
          </button>
        </form>
      </div>
    </div>
  );
}
